import React, { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import Alert from '@material-ui/lab/Alert';
import get from 'lodash/get';
import strings from 'strings';
import { TextBox } from 'UI/components/atoms';
import FPHint from 'UI/components/atoms/FPHint';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPTextEditor from 'UI/components/molecules/FPTextEditor';
import { useBulkSmartTags } from 'UI/components/organisms/BulkEmailDrawer/BulkEmailDrawerSmartTags.hook';
import { BulkFormFieldGrid } from 'UI/components/organisms/BulkEmailForm/components/BulkFormFieldGrid';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';
import { tinymceBulkConfig } from 'UI/constants/config';
import { idOptionSelected } from 'UI/utils';
import { LabelRenderers } from 'UI/utils/renderers';

import { buildAttemptsFormKey } from '../../BulkSequences.utils';

const DEFAULT_TRIGGER_DAYS_LENGTH = 30;

const buildTriggerOption = ({ day, recommendedTriggerDay, scopeTitle, step }) => {
  const recommendedSuffix =
    recommendedTriggerDay === day
      ? strings.formatString(sequencesCopies.sections.triggerDays.options.recommendedSuffix, {
          bulkScope: scopeTitle,
          step
        })
      : '';
  const dayLabel = day > 1 ? 'Days' : 'Day';
  const finalTitle = strings.formatString(sequencesCopies.sections.triggerDays.options.title, {
    day,
    dayLabel,
    recommendedSuffix
  });

  return {
    id: day,
    title: finalTitle,
    value: day
  };
};

const {
  sequences: sequencesCopies,
  fields: fieldsCopies,
  signatureInfo
} = strings.bulkEmails.forms;

export const BulkAttemptForm = ({
  attemptId,
  onFetchingSmartTags = () => {},
  recommendedTriggerDay,
  step
}) => {
  const editorRef = useRef(null);
  const paths = useMemo(() => {
    const baseFormKey = buildAttemptsFormKey(attemptId);
    return {
      subject: `${baseFormKey}.subject`,
      emailBody: `${baseFormKey}.emailBody`,
      triggerDays: `${baseFormKey}.triggerDays`
    };
  }, [attemptId]);

  const { errors, getValues, register, setValue, unregister, watch } = useFormContext();
  const watchers = watch([paths.emailBody, paths.subject, paths.triggerDays]);
  const scopeTitle = watch(BulkDrawerFormFieldsMap.BulkScope.key)?.title || '';
  const smartTagsWatcher = watch(BulkDrawerFormFieldsMap.SmartTags.key);

  const defaultTriggerDays = useMemo(
    () =>
      Array.from({ length: DEFAULT_TRIGGER_DAYS_LENGTH }, (_, index) =>
        buildTriggerOption({
          day: index + 1,
          recommendedTriggerDay,
          scopeTitle,
          step
        })
      ),
    [recommendedTriggerDay, scopeTitle, step]
  );

  useBulkSmartTags({
    getValues,
    onFetchingSmartTags,
    register,
    registrationConfig: {
      subject: {
        key: paths.subject,
        requiredMessage: sequencesCopies.sections.subject.requiredMessage,
        fieldName: 'attempt subject'
      },
      emailBody: {
        key: paths.emailBody,
        requiredMessage: sequencesCopies.sections.emailBody.requiredMessage,
        fieldName: 'attempt email body'
      }
    },
    setValue
  });

  useEffect(() => {
    register(
      { name: paths.triggerDays },
      { required: sequencesCopies.sections.triggerDays.requiredMessage }
    );
  }, [paths.triggerDays, register]);

  useEffect(() => {
    return () => {
      unregister([paths.subject, paths.emailBody, paths.triggerDays]);
    };
  }, [paths, unregister]);

  const handleSubjectChange = (_, value) => {
    setValue(paths.subject, value, true);
  };

  const handleEmailBodyChange = value => {
    setValue(paths.emailBody, value, true);
  };

  const handleSelectTriggerdays = (name, value) => {
    setValue(name, value, true);
  };

  return (
    <>
      <Alert severity="info">{sequencesCopies.alerts.recipientsToReach}</Alert>
      <BulkFormFieldGrid
        label={sequencesCopies.sections.trigger.label}
        field={sequencesCopies.sections.trigger.field}
        shouldShowField
      />
      <BulkFormFieldGrid
        label={sequencesCopies.sections.triggerDays.label}
        field={
          <AutocompleteSelect
            defaultOptions={defaultTriggerDays}
            error={!!get(errors, paths.triggerDays, false)}
            errorText={get(errors, paths.triggerDays, {})?.message}
            getOptionLabel={option => option?.title || ''}
            getOptionSelected={idOptionSelected}
            name={paths.triggerDays}
            onSelect={handleSelectTriggerdays}
            placeholder={sequencesCopies.sections.triggerDays.placeholder}
            selectedValue={watchers[paths.triggerDays] ?? null}
          />
        }
        shouldShowField
      />
      <FPHint variant="warning" description={sequencesCopies.sections.triggerDays.hint} size="lg" />
      <Alert severity="info">{sequencesCopies.alerts.quota}</Alert>
      <BulkFormFieldGrid
        label={sequencesCopies.sections.subject.label}
        field={
          <TextBox
            name={paths.subject}
            value={get(watchers, paths.subject, '')}
            placeholder={sequencesCopies.sections.subject.label}
            onChange={handleSubjectChange}
            error={!!get(errors, paths.subject, false)}
            errorText={get(errors, paths.subject, {})?.message}
          />
        }
        shouldShowField
      />
      <BulkFormFieldGrid
        label={fieldsCopies.smartTags.label}
        hint={fieldsCopies.smartTags.hint}
        field={
          <AutocompleteSelect
            /**
             * DirtyHack: added props selectedValue=[] and multiple=true in order to avoid
             * autocomplete selected value console warnings and to keep current functionality
             * of selecting an option without setting the currently selected choice as input's
             * value.
             */
            multiple
            selectedValue={[]}
            placeholder={fieldsCopies.smartTags.placeholder}
            placeholderAsLabel={false}
            onSelect={(_, value) => editorRef.current.insertContent(value[0].value)}
            getOptionDisabled={option => option.value === signatureInfo.selectOption.value}
            defaultOptions={smartTagsWatcher}
            getOptionLabel={LabelRenderers.nameOrEmpty}
          />
        }
      />
      <FPTextEditor
        onInit={(e, editor) => {
          editorRef.current = editor;
        }}
        config={tinymceBulkConfig}
        value={get(watchers, paths.emailBody, '')}
        error={!!get(errors, paths.emailBody, false)}
        errorText={get(errors, paths.emailBody, {})?.message}
        onChange={handleEmailBodyChange}
      />
    </>
  );
};
